/**
 * Decoupled version of LegendCopy component for report generation
 */

import { GeckoColorScale } from 'color-scale-kit'
import { Draggable } from 'components/Draggable'
import { AssetVizSceneConfig } from 'features/assetViz'
import React from 'react'
import styled from 'styled-components'
import { Vector2 } from 'three'
import {
  GeckoComponentDataFrame,
  GeckoMeasurementRange,
  GeckoMeasurementUnit,
} from 'types'

const Title = styled.div`
  position: relative;
  width: 120px;
  font-size: 16px;
  margin-bottom: 10px;
`

const MainContent = styled.div`
  position: relative;
`

const BarContainer = styled.div<{ $color: string }>`
  position: relative;
  height: 300px;
  width: 60px;
  border: 1px solid ${({ $color }) => $color};
`

const Label = styled.div<{ $x: string; $y: string }>`
  position: absolute;
  left: ${({ $x }) => $x};
  top: ${({ $y }) => $y};
  width: max-content;
`

const LegendBar: React.FC<{
  selectedColorScale: GeckoColorScale
  grayscale: boolean
  visibleFractionRange: Vector2
}> = ({ selectedColorScale, grayscale, visibleFractionRange }) => {
  return (
    <svg
      style={{
        position: 'absolute',
        clipPath: `xywh(0 ${(1 - visibleFractionRange.y) * 100}% 100% ${
          (visibleFractionRange.y - visibleFractionRange.x) * 100
        }%)`,
        filter: grayscale ? 'grayscale(1)' : '',
      }}
      viewBox={`0 0 1 296`}
      width='100%'
      height='100%'
      preserveAspectRatio='none'>
      <defs>
        <pattern
          id='legendBackground'
          patternUnits='userSpaceOnUse'
          width={296}
          height={1}>
          <image
            href={selectedColorScale?.getTextureURL()}
            x={0}
            y={0}
            preserveAspectRatio='none'
            height={1}
            width={296}
          />
        </pattern>
      </defs>
      <rect
        fill='url(#legendBackground)'
        style={{
          transform: `translate(0, 296px) rotate(-90deg)`,
        }}
        height={1}
        width={296}
      />
    </svg>
  )
}

export interface LegendCopyProps {
  cdf: GeckoComponentDataFrame
  sceneConfig: AssetVizSceneConfig
  colorScale?: GeckoColorScale
  valueRange?: GeckoMeasurementRange
  filteredValueRange?: GeckoMeasurementRange
  preferredUnit?: GeckoMeasurementUnit
}

export const LegendCopy: React.FC<LegendCopyProps> = ({
  cdf,
  sceneConfig,
  colorScale,
  valueRange,
  filteredValueRange,
  preferredUnit,
}) => {
  // Use provided color scale or create default
  const selectedColorScale = React.useMemo(() => {
    if (colorScale) return colorScale
    // Create a default color scale if not provided
    const colorcet = cdf.data.params.view.colorcet
    if (colorcet?.customColorScale) {
      return new GeckoColorScale({
        customColorScale: colorcet.customColorScale,
      })
    }
    return new GeckoColorScale({})
  }, [colorScale, cdf])

  // Use provided preferred unit or fall back to CDF unit
  const displayUnit = preferredUnit || cdf.data.params.measurement_unit

  // Calculate ticks for the legend
  const ticks: [number, string][] = React.useMemo(() => {
    if (!valueRange || !displayUnit) return []

    const range = valueRange.convertTo(displayUnit)

    const rangeScale = Math.ceil(Math.log10(range.size.num))
    const precision = Math.max(3 - rangeScale, 0)
    if (!isFinite(precision)) return []

    const lowerPrecision = Math.min(Math.max(0, precision - 1), 100)
    const increment = parseFloat((range.size.num / 10).toFixed(lowerPrecision))

    const ticks: [number, string][] = []
    ticks.push([0, range.max.num.toFixed(precision)])
    const mid =
      Math.round(
        (range.min.num + range.size.num / 2) * Math.pow(10, lowerPrecision)
      ) / Math.pow(10, lowerPrecision)
    let val = mid + increment * 5
    for (; val > range.min.num; val -= increment) {
      const percent = (range.max.num - val) / range.size.num
      if (percent < 0.045 || percent > 0.955) continue
      ticks.push([percent, val.toFixed(lowerPrecision)])
    }
    ticks.push([1, range.min.num.toFixed(precision)])
    return ticks
  }, [valueRange, displayUnit])

  const textColor = sceneConfig?.invertColors ? 'black' : 'white'

  // Calculate visible fraction for filtered value range
  const visibleFractionRange = React.useMemo(() => {
    if (!filteredValueRange || !valueRange) {
      return new Vector2(0, 1)
    }
    return new Vector2(
      (filteredValueRange.min.num - valueRange.min.num) / valueRange.size.num,
      1 -
        (valueRange.max.num - filteredValueRange.max.num) / valueRange.size.num
    )
  }, [filteredValueRange, valueRange])

  //------------------------------------------------------------------------------------------

  return (
    <Draggable
      style={{
        display: sceneConfig.showLegend ? 'block' : 'none',
      }}>
      <Title>
        {cdf.data.params.view.name} ({displayUnit.name})
      </Title>
      <MainContent>
        {ticks.map(function (tick) {
          return (
            <Label $x={'59px'} $y={tick[0] * 280 + 'px'} key={tick[0]}>
              - &nbsp; {tick[1]}
            </Label>
          )
        })}
        <BarContainer $color={textColor}>
          <LegendBar
            selectedColorScale={selectedColorScale}
            grayscale={true}
            visibleFractionRange={new Vector2(0, 1)}
          />
          <LegendBar
            selectedColorScale={selectedColorScale}
            grayscale={false}
            visibleFractionRange={visibleFractionRange}
          />
        </BarContainer>
      </MainContent>
    </Draggable>
  )
}
